import { normalizeBusinessId } from './business-id.js';
/**
 * How far the invoice may be off and still count as a match, in ₪.
 *
 * Not zero: the coach's invoicing software rounds VAT its own way, so a total
 * that differs by an agora or two is the SAME number in every sense that
 * matters. ₪1 absorbs that while still catching any real discrepancy — the
 * mistakes this feature exists to catch (last month's invoice, the pre-VAT
 * subtotal, a typo'd digit) are never within a shekel.
 */
export const INVOICE_AMOUNT_TOLERANCE = 1;
/**
 * Compare a read invoice total against the owed total.
 *
 * `expected` is the salary line's `computation.total` — the invoice amount, VAT
 * included, BEFORE ניכוי מס במקור. That's deliberate: withholding is deducted
 * from the payment, not from the invoice, so the coach still bills the full
 * amount (see tax.ts). `actual` is null when nothing could be read.
 */
export function evaluateInvoiceAmount(actual, expected) {
    if (actual == null || !Number.isFinite(actual))
        return 'unreadable';
    return Math.abs(actual - expected) <= INVOICE_AMOUNT_TOLERANCE ? 'match' : 'mismatch';
}
/**
 * Whether this check result must stop the coach from approving.
 *
 * ONLY a confident mismatch under 'block' does. An unreadable invoice never
 * blocks — a blurry photo or an OpenAI outage must not strand a coach or stall
 * the whole box's payroll behind a model we don't control. The operator still
 * sees "לא נבדק" on the line and can chase it.
 */
export function invoiceCheckBlocks(mode, status) {
    return mode === 'block' && status === 'mismatch';
}
// ── the non-amount checks ────────────────────────────────────────────────────
/**
 * חשבוניות ישראל: a tax invoice from an עוסק מורשה must carry a מספר הקצאה from
 * the Tax Authority when its pre-VAT amount EXCEEDS the threshold in force on the
 * invoice date — otherwise the box can't deduct the input VAT. Dated so a lower
 * threshold is one new row, and an older invoice is judged by its own rule.
 */
export const ALLOCATION_THRESHOLDS = [
    { from: '2024-05-05', above: 25_000 },
    { from: '2025-01-01', above: 20_000 },
    { from: '2026-01-01', above: 10_000 },
    { from: '2026-06-01', above: 5_000 },
];
/** Whether an invoice for this line must carry a מספר הקצאה. `vat` > 0 means the
 *  line is billed with VAT, i.e. the coach is an עוסק מורשה issuing a tax invoice
 *  (a פטור or a שכיר never does). `subtotal` is the PRE-VAT amount; `dateISO` is
 *  the invoice date (YYYY-MM-DD). Read off the frozen computation, so it agrees
 *  with the amount the coach was asked to bill. */
export function allocationNumberRequired(vat, subtotal, dateISO) {
    if (!(vat > 0))
        return false;
    const rule = [...ALLOCATION_THRESHOLDS].reverse().find((t) => dateISO >= t.from);
    return !!rule && subtotal > rule.above;
}
/** A מספר הקצאה is at least the 9-digit number itself; some invoicing software
 *  prints a longer form with a date prefix. Digits only once spaces/dashes go. */
export function isPlausibleAllocationNumber(v) {
    const digits = (v ?? '').replace(/[\s-]/g, '');
    return /^\d{9,27}$/.test(digits);
}
/**
 * Run every check against what was read. The SINGLE source of truth for the
 * verdict — the server's block decision and the coach's wording both come from
 * this result.
 *
 * Ids are matched against EVERY number on the document, not the role the model
 * assigned it: models swap "issuer" and "customer" on RTL layouts, and what we
 * actually need to know is "does the coach's number appear" / "does the box's
 * number appear". An invoice issued by someone else lacks the first; one
 * addressed to another unit lacks the second.
 */
export function evaluateInvoice(read, exp) {
    const amountStatus = evaluateInvoiceAmount(read?.total, exp.total);
    // Couldn't read the document at all → no field verdicts: "missing" would only
    // mean "we couldn't see", and that must never block (fails open).
    if (!read || amountStatus === 'unreadable') {
        return { status: 'unreadable', details: { issues: [], read } };
    }
    const issues = [];
    if (amountStatus === 'mismatch') {
        issues.push({ field: 'amount', problem: 'mismatch', expected: exp.total, found: read.total });
    }
    const onDoc = new Set(read.business_ids.map(normalizeBusinessId).filter((x) => !!x));
    const idCheck = (field, expected, found) => {
        const want = normalizeBusinessId(expected);
        if (!want || onDoc.has(want))
            return;
        issues.push({ field, problem: onDoc.size ? 'mismatch' : 'missing', expected: want, found });
    };
    idCheck('issuer_id', exp.issuer_id, read.issuer_id);
    idCheck('recipient_id', exp.recipient_id, read.recipient_id);
    if (exp.allocation_required) {
        if (!read.allocation_number)
            issues.push({ field: 'allocation_number', problem: 'missing' });
        else if (!isPlausibleAllocationNumber(read.allocation_number)) {
            issues.push({ field: 'allocation_number', problem: 'invalid', found: read.allocation_number });
        }
    }
    return { status: issues.length ? 'mismatch' : 'match', details: { issues, read } };
}
const fmtMoney = (n) => typeof n === 'number' ? `${n.toLocaleString('he-IL', { maximumFractionDigits: 2 })} ₪` : '—';
/** One Hebrew sentence per issue — shown to the coach on the approval page and
 *  to the operator on the line. */
export function describeInvoiceIssue(i) {
    switch (i.field) {
        case 'amount':
            return `הסכום בחשבונית (${fmtMoney(i.found)}) אינו תואם לסכום לתשלום (${fmtMoney(i.expected)}).`;
        case 'issuer_id':
            return i.problem === 'missing'
                ? `לא נמצא בחשבונית מספר עוסק. החשבונית צריכה להיות מופקת על מספר העוסק ${i.expected}.`
                : `מספר העוסק בחשבונית אינו תואם למספר העוסק הרשום (${i.expected}).`;
        case 'recipient_id':
            return i.problem === 'missing'
                ? `לא נמצא בחשבונית ח״פ של הלקוח. יש להפנות את החשבונית לח״פ ${i.expected}.`
                : `החשבונית אינה מופנית לח״פ הנכון. יש להפנות אותה לח״פ ${i.expected}.`;
        case 'allocation_number':
            return i.problem === 'missing'
                ? 'לא נמצא בחשבונית מספר הקצאה. חשבונית מס בסכום הזה חייבת לכלול מספר הקצאה מרשות המסים.'
                : `מספר ההקצאה בחשבונית (${i.found}) אינו תקין.`;
    }
}
/** Short Hebrew label for the operator's report row / drawer. */
export const INVOICE_CHECK_LABELS = {
    match: 'החשבונית תקינה',
    mismatch: 'נמצאו בעיות בחשבונית',
    unreadable: 'לא ניתן לקרוא את החשבונית',
};
/** The three settings options, with the explanation shown under each radio. */
export const INVOICE_AMOUNT_CHECK_OPTIONS = [
    {
        value: 'none',
        label: 'לא לבדוק',
        desc: 'החשבונית שאיש הצוות מעלה נשמרת כמו שהיא, ללא בדיקה על ידי המערכת.',
    },
    {
        value: 'warn',
        label: 'להתריע על בעיה',
        desc: 'המערכת בודקת את הסכום, את מספר העוסק של איש הצוות, את הח״פ שהחשבונית מופנית אליו ואת מספר ההקצאה (כשהוא נדרש). אם משהו לא תקין תוצג התראה, אך איש הצוות יוכל לאשר בכל זאת.',
    },
    {
        value: 'block',
        label: 'לחסום אישור עד לתיקון',
        desc: 'אותן בדיקות, אבל לא ניתן לאשר את הדוח עד שתועלה חשבונית תקינה. אם לא ניתן לקרוא את החשבונית, האישור לא ייחסם.',
    },
];
//# sourceMappingURL=invoice-check.js.map